import * as THREE from 'three'
import { WAYPOINTS } from './waypoints.ts'

// The spec asks for getPointAt(progress) (arc-length parameterized) AND for waypoints
// pinned to specific progress values. Those conflict: arc-length parameterization ignores
// the waypoint spacing. Resolution (ISSUES.md #4): compute each waypoint's arc-length
// fraction u_i, then remap progress piecewise [p_i..p_i+1] -> [u_i..u_i+1] before calling
// getPointAt. Constant speed within a segment, waypoints land exactly on their beats.

const posCurve = new THREE.CatmullRomCurve3(WAYPOINTS.map((w) => new THREE.Vector3(...w.pos)))
const tgtCurve = new THREE.CatmullRomCurve3(WAYPOINTS.map((w) => new THREE.Vector3(...w.tgt)))
const P = WAYPOINTS.map((w) => w.p)

function arcFractions(curve: THREE.CatmullRomCurve3, n: number): number[] {
  const div = 1200
  const pts = curve.getPoints(div)
  const cum: number[] = [0]
  for (let i = 1; i <= div; i++) cum.push(cum[i - 1] + pts[i].distanceTo(pts[i - 1]))
  const total = cum[div]
  const us: number[] = []
  for (let i = 0; i < n; i++) {
    const idx = Math.round((i / (n - 1)) * div)
    us.push(cum[idx] / total)
  }
  return us
}

const posU = arcFractions(posCurve, WAYPOINTS.length)
const tgtU = arcFractions(tgtCurve, WAYPOINTS.length)

function remap(p: number, us: number[]): number {
  const pc = Math.min(1, Math.max(0, p))
  for (let i = 0; i < P.length - 1; i++) {
    if (pc <= P[i + 1]) {
      const t = (pc - P[i]) / (P[i + 1] - P[i])
      return us[i] + (us[i + 1] - us[i]) * t
    }
  }
  return 1
}

const easeInOutCubic = (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2)

export function cameraAt(p: number, outPos: THREE.Vector3, outTgt: THREE.Vector3) {
  posCurve.getPointAt(remap(p, posU), outPos)
  tgtCurve.getPointAt(remap(p, tgtU), outTgt)
}

// SPEC §3: fov interpolates on a slightly narrower window than the position curve,
// eased, so the lens change leads the move.
export function fovAt(p: number): number {
  const pc = Math.min(1, Math.max(0, p))
  for (let i = 0; i < P.length - 1; i++) {
    if (pc <= P[i + 1]) {
      let t = (pc - P[i]) / (P[i + 1] - P[i])
      t = Math.min(1, Math.max(0, (t - 0.12) / 0.76)) // narrower window
      return WAYPOINTS[i].fov + (WAYPOINTS[i + 1].fov - WAYPOINTS[i].fov) * easeInOutCubic(t)
    }
  }
  return WAYPOINTS[WAYPOINTS.length - 1].fov
}
